// Thin wrapper around the Telegram Mini Apps JS SDK. Every call
// is defensive (optional chaining + try/catch) so the app runs
// identically in a plain desktop browser during development.

import type { TelegramUser } from '@/types/user';

export type HapticImpactStyle = 'light' | 'medium' | 'heavy' | 'rigid' | 'soft';
export type HapticNotificationType = 'success' | 'warning' | 'error';

interface RawTelegramUser {
  id: number;
  first_name: string;
  last_name?: string;
  username?: string;
  photo_url?: string;
}

interface TelegramHapticFeedback {
  impactOccurred: (style: HapticImpactStyle) => void;
  notificationOccurred: (type: HapticNotificationType) => void;
  selectionChanged: () => void;
}

export interface TelegramWebApp {
  ready: () => void;
  expand: () => void;
  setHeaderColor?: (color: string) => void;
  setBackgroundColor?: (color: string) => void;
  initDataUnsafe?: { user?: RawTelegramUser };
  HapticFeedback?: TelegramHapticFeedback;
}

declare global {
  interface Window {
    Telegram?: { WebApp: TelegramWebApp };
  }
}

export function getTelegramWebApp(): TelegramWebApp | null {
  if (typeof window === 'undefined') return null;
  return window.Telegram?.WebApp ?? null;
}

export function isRunningInTelegram(): boolean {
  return getTelegramWebApp() !== null;
}

export function initTelegram(): void {
  const tg = getTelegramWebApp();
  if (!tg) return;
  try {
    tg.ready();
    tg.expand();
    tg.setHeaderColor?.('#0c0a14');
    tg.setBackgroundColor?.('#0c0a14');
  } catch {
    // Telegram SDK is best-effort; never let it break the app shell.
  }
}

export function getTelegramUser(): TelegramUser | null {
  const raw = getTelegramWebApp()?.initDataUnsafe?.user;
  if (!raw) return null;
  return {
    id: raw.id,
    firstName: raw.first_name,
    lastName: raw.last_name,
    username: raw.username,
    photoUrl: raw.photo_url,
  };
}

export function hapticImpact(style: HapticImpactStyle = 'light'): void {
  try {
    getTelegramWebApp()?.HapticFeedback?.impactOccurred(style);
  } catch {
    /* no-op outside Telegram */
  }
}

export function hapticNotification(type: HapticNotificationType): void {
  try {
    getTelegramWebApp()?.HapticFeedback?.notificationOccurred(type);
  } catch {
    /* no-op outside Telegram */
  }
}

export function hapticSelect(): void {
  try {
    getTelegramWebApp()?.HapticFeedback?.selectionChanged();
  } catch {
    /* no-op outside Telegram */
  }
}
